import React from 'react'

import { Box, Href, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import Compensation, { CUSTOM_TIME_CONDITION } from 'modules/contracts/store/models/Compensation'

import { FormSection } from './_FormSection'

interface Props {
  form: Form
  employeeLegalEntityCurrency: string
  isEditable: boolean
}

const Definition = ({ form, employeeLegalEntityCurrency, isEditable }: Props) => {
  const legalEntityContractData = useLegalEntityContractData()

  const taxonomyOptions =
    legalEntityContractData.commonContractData.compensationTaxonomies.actives.map((item) => ({
      label: item.attributes.name,
      value: item.attributes.id,
    }))

  const types = Compensation.Types()

  const typeOptions: SelectOption[] = Object.keys(types).map((key) => ({
    label: types[key] ?? '',
    value: key,
  }))

  const isPerWorkedDay = form.get('compensation_type').value === Compensation.types.per_worked_day

  const calculationOptions = (): SelectOption[] => {
    const calculationTypes = Compensation.CalculationTypes()

    return Object.keys(calculationTypes).map((key) => ({
      label: calculationTypes[key] ?? '',
      value: key,
    }))
  }

  const timeConditionOptions = (): SelectOption[] => {
    const timeConditionType = Compensation.TimeConditionTypes()
    return Object.entries(timeConditionType).map(([key, label]) => ({
      label: label ?? '',
      value: key,
    }))
  }

  const amountFieldLabel = (): string => {
    if (form.get('compensation_type').value === Compensation.types.per_worked_day) {
      return i18n.t(
        'contract_versions.forms.additional_compensation.modal.amount_per_worked_day_field'
      )
    }

    return i18n.t('contract_versions.forms.additional_compensation.modal.amount_field')
  }

  const unitOptions = (employeeLeCurrency: string): SelectOption[] => {
    const units = Compensation.UnitOptions(employeeLeCurrency)

    return Object.keys(units).map((key) => ({
      label: units[key] ?? '',
      value: key,
    }))
  }

  const handleTypeChange = () => {
    if (form.get('compensation_type').value === Compensation.types.undefined) {
      form.setValues({ amount: null })
    }
  }

  const isAmountReadOnly =
    !isEditable || form.get('compensation_type').value === Compensation.types.undefined

  const TooltipDialog = () => {
    const [open, toggle] = useToggle(false)

    const explanationItems = React.useRef([
      {
        title: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.undefined.title'
        ),
        description: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.undefined.description'
        ),
      },
      {
        title: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.fixed.title'
        ),
        description: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.fixed.description'
        ),
      },
      {
        title: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.up_to.title'
        ),
        description: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.up_to.description'
        ),
      },
      {
        title: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.per_worked_day.title'
        ),
        description: i18n.t(
          'contract_versions.forms.additional_compensation.modal.explanation.per_worked_day.description'
        ),
      },
    ]).current

    return (
      <Tooltip.ItemList open={open} toggle={toggle} attachment="top center">
        <Icon.Small icon="question" encircled={'grey800'} />
        {explanationItems.map((item) => {
          return (
            <Box textAlign="left" key={item.title}>
              <Text.Normal size="200" weight="bold">
                {item.title.toUpperCase()}
              </Text.Normal>
              <Box paddingTop="s2">
                <Text.Normal size="200">{item.description}</Text.Normal>
              </Box>
            </Box>
          )
        })}
        <Box paddingTop="s8">
          <Box
            maxWidth="fit-content"
            textAlign="left"
            paddingY="s4"
            paddingX="s12"
            borderRadius={{ all: 'abs100' }}
            border={{ all: { style: 'solid', width: 's1', color: 'grey700' } }}
          >
            <Href href={helpCenterUrl('additional_compensation_types')} target="_blank">
              <Text.Normal size="100">
                {i18n.t(
                  'contract_versions.forms.additional_compensation.modal.explanation.learn_more'
                )}
              </Text.Normal>
            </Href>
          </Box>
        </Box>
      </Tooltip.ItemList>
    )
  }

  return (
    <FormSection
      header={i18n.t('contract_versions.forms.additional_compensation.modal.section1_header')}
    >
      <Select
        mandatory
        label={i18n.t('contract_versions.forms.additional_compensation.modal.taxonomy')}
        field={form.get('contracts_taxonomy_id')}
        options={taxonomyOptions}
        none={i18n.t('common.none')}
        readonly={!isEditable}
      />

      <TextArea
        field={form.get('description')}
        label={i18n.t('contract_versions.forms.additional_compensation.modal.description_field')}
      />

      <Select
        label={i18n.t('contract_versions.forms.additional_compensation.modal.compensation_type')}
        field={form.get('compensation_type')}
        secondaryLabel={<TooltipDialog />}
        options={typeOptions}
        onChange={handleTypeChange}
        readonly={!isEditable}
      />

      {isPerWorkedDay && (
        <>
          <Select
            label={i18n.t(
              'contract_versions.forms.additional_compensation.modal.calculation_field'
            )}
            field={form.get('calculation')}
            options={calculationOptions()}
            readonly={!isEditable}
          />
          <Select
            label={i18n.t('contract_versions.forms.additional_compensation.modal.time_condition')}
            field={form.get('time_condition')}
            options={timeConditionOptions()}
            none={i18n.t('common.none')}
            mandatory={isPerWorkedDay}
          />
          {form.get('time_condition').value === CUSTOM_TIME_CONDITION && (
            <TextInput
              label={i18n.t('contract_versions.forms.additional_compensation.modal.custom')}
              field={form.get('minimum_amount_of_hours')}
              mandatory={
                form.get('time_condition').value === CUSTOM_TIME_CONDITION && isPerWorkedDay
              }
            />
          )}
        </>
      )}

      <Box flexDirection="row" gap="s16">
        <TextInput
          label={amountFieldLabel()}
          mandatory={!isAmountReadOnly}
          field={form.get('amount')}
          readonly={isAmountReadOnly}
        />
        <Select
          label={i18n.t('contract_versions.forms.additional_compensation.modal.unit_field')}
          field={form.get('unit')}
          options={unitOptions(employeeLegalEntityCurrency)}
          readonly={!isEditable}
        />
      </Box>
    </FormSection>
  )
}

export default observer(Definition)
